import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { haptic } from "@/lib/native";
import { maybeRequestAppReview } from "@/lib/appReview";
import { useAuth } from "@/contexts/AuthContext";
import { iapSupported, getIapProduct, purchaseIapProduct, IAP_PRODUCTS } from "@/lib/revenuecat";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";

/**
 * IapConsumableButton — Apple In-App Purchase button for one-time
 * consumables (dog registration, litter registration, certificate).
 *
 * Flow: purchase via StoreKit → RevenueCat webhook grants a credit on
 * the user document → we poll /billing/iap-credits until it lands →
 * onGranted() fires so the caller can submit (checkout consumes the
 * credit server-side and fulfils for free).
 *
 * Falls back to IosPurchaseNotice when StoreKit isn't available or the
 * product can't be loaded.
 */
export default function IapConsumableButton({ creditKey, label, noticeLabel, onGranted, className = "" }) {
  const { user } = useAuth();
  const [state, setState] = useState("loading"); // loading | ready | failed
  const [product, setProduct] = useState(null);
  const [credits, setCredits] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/iap-credits");
        if (!cancelled) setCredits(data[creditKey] || 0);
      } catch {}
      if (!iapSupported() || !user?.id) {
        if (!cancelled) setState("failed");
        return;
      }
      try {
        const p = await getIapProduct(user.id, IAP_PRODUCTS[creditKey]);
        if (!cancelled) {
          setProduct(p);
          setState(p ? "ready" : "failed");
        }
      } catch {
        if (!cancelled) setState("failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creditKey, user?.id]);

  const waitForCredit = async (baseline) => {
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const { data } = await api.get("/billing/iap-credits");
        if ((data[creditKey] || 0) > baseline) return true;
      } catch {}
    }
    return false;
  };

  const buy = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const baseline = credits;
      await purchaseIapProduct(user.id, product);
      haptic("medium");
      toast.success("Purchase received — finishing up…");
      const ok = await waitForCredit(baseline);
      if (ok) {
        setCredits(baseline + 1);
        onGranted?.();
        maybeRequestAppReview("consumable_purchase");
      } else {
        toast.info("Purchase confirmed — it may take a minute to appear. Please try again shortly.");
      }
    } catch (e) {
      if (e?.userCancelled || /cancel/i.test(e?.message || "")) return;
      toast.error("Purchase didn't complete. You were not charged.");
    } finally {
      setBusy(false);
    }
  };

  if (credits > 0) {
    return (
      <div className={className}>
        <button
          type="button"
          onClick={() => onGranted?.()}
          className="w-full rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-11 px-6 text-[14px] font-semibold transition-colors"
          data-testid={`iap-use-credit-${creditKey}`}
        >
          {label} — use your purchased credit
        </button>
      </div>
    );
  }

  if (state === "loading") return null;

  if (state === "failed") {
    return <IosPurchaseNotice label={noticeLabel || label} className={className} />;
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={buy}
        disabled={busy}
        className="w-full rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] disabled:opacity-60 text-white h-11 px-6 text-[14px] font-semibold transition-colors"
        data-testid={`iap-buy-${creditKey}`}
      >
        {busy ? "Processing…" : `${label} — ${product.priceString}`}
      </button>
      <p className="text-[11px] text-[#9CA3AF] mt-1.5 text-center">
        One-time purchase billed through Apple.
      </p>
    </div>
  );
}
